import { WebSocketEventManager } from './services/websocketEvents';

interface Listeners {
  handleTranscriptChanges?: (
    history: Array<{ role: string; message: string }>,
    content: { role: string; message: string },
  ) => void;
  handleAudioResponseStarted?: () => void;
  handleAudioResponseFinished?: () => void;
}

export class VoiceStreaming {
  private wsManager: WebSocketEventManager | null;

  private systemPrompt: string;
  /**
   * Subscriber on main event from WS responces
   * @private
   */
  private listeners: Listeners;

  constructor(listeners: Listeners, options?) {
    this.wsManager = null;
    this.systemPrompt = '';
    this.listeners = listeners;
  }

  // Start streaming audio
  async startStreaming(): Promise<void> {
    // Initialize WebSocket manager with the key
    this.wsManager = new WebSocketEventManager();

    // Add custom handlers
    this.wsManager.onAudioReceived = this.handleAudioReceived;
    this.wsManager.onAudioResponseStarted = this.listeners?.handleAudioResponseStarted;
    this.wsManager.onAudioResponseFinished = this.listeners?.handleAudioResponseFinished;
    this.wsManager.onUpdateTranscript = this.listeners?.handleTranscriptChanges;
    this.wsManager.onUpdateStatus = this.updateStatus;

    // Pass the current system prompt to the WebSocketEventManager
    this.wsManager.setSystemPrompt(this.systemPrompt);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono
          sampleRate: 16000, // 16kHz
          sampleSize: 16, // 16-bit
          echoCancellation: true, // Enable echo cancellation
          noiseSuppression: true, // Enable noise suppression
          autoGainControl: true, // Enable automatic gain control
        },
      });

      // Create AudioContext for processing
      const audioContext = new AudioContext({
        sampleRate: 16000,
        latencyHint: 'interactive',
      });

      // Create MediaStreamSource
      const source = audioContext.createMediaStreamSource(stream);

      // Create ScriptProcessor for raw PCM data
      // Note: ScriptProcessorNode is deprecated but still widely used
      // TypeScript doesn't fully type it, so we need to use any in some places
      const processor = audioContext.createScriptProcessor(1024, 1, 1);

      source.connect(processor);
      processor.connect(audioContext.destination);

      // Variables for user speech detection
      let userIsSpeaking = false;
      let silenceTimer: number | null = null;
      let speakingStarted = false;
      const SILENCE_THRESHOLD = 0.01;
      const SPEECH_THRESHOLD = 0.015; // Slightly higher threshold to confirm speech
      const SILENCE_DURATION = 1000; // 1 second of silence to mark end of speech
      const MIN_SPEECH_SAMPLES = 5; // Need multiple samples to confirm speech

      // Counter to confirm speech
      let speechSampleCount = 0;

      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        const inputData = e.inputBuffer.getChannelData(0);

        // Convert Float32Array to Int16Array
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          // Convert float to 16-bit integer
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Calculate audio level for this chunk
        const audioLevel = Math.max(...Array.from(inputData).map(Math.abs));

        // Speech detection logic
        if (audioLevel > SPEECH_THRESHOLD) {
          // Potential speech detected
          speechSampleCount++;

          // If we have enough speech samples, confirm the user is speaking
          if (speechSampleCount >= MIN_SPEECH_SAMPLES && !userIsSpeaking) {
            userIsSpeaking = true;
            if (this.wsManager && !speakingStarted) {
              this.wsManager.startUserTalking();
              speakingStarted = true;
            }
          }

          // Reset silence timer if active
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }
        } else if (audioLevel < SILENCE_THRESHOLD && userIsSpeaking) {
          // Potential silence detected
          speechSampleCount = 0;

          // Start silence timer if not already running
          if (!silenceTimer) {
            silenceTimer = window.setTimeout(() => {
              // After SILENCE_DURATION, mark user as stopped talking
              userIsSpeaking = false;
              speakingStarted = false;
              if (this.wsManager) {
                this.wsManager.stopUserTalking();
              }
              silenceTimer = null;
            }, SILENCE_DURATION);
          }
        } else {
          // Reset speech counter during ambiguous audio levels
          speechSampleCount = 0;
        }

        // Convert to base64
        const base64data = btoa(String.fromCharCode.apply(null, new Uint8Array(pcmData.buffer)));

        // Send to WebSocket
        if (this.wsManager) {
          this.wsManager.sendAudioChunk(base64data);
        }
      };

      // Store cleanup functions
      window.audioCleanup = () => {
        // Ensure we stop tracking user speech on cleanup
        if (this.wsManager && userIsSpeaking) {
          this.wsManager.stopUserTalking();
        }

        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }

        processor.disconnect();
        source.disconnect();
        stream.getTracks().forEach((track) => track.stop());
      };
    } catch (error) {
      console.error(
        'Error accessing microphone:',
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  updateStatus(message: string, classname: string): void {
    console.log('Status updated:', message, classname);
  }

  // Stop streaming audio
  stopStreaming(): void {
    // Cleanup audio processing
    if (window.audioCleanup) {
      window.audioCleanup();
    }

    if (this.wsManager) {
      this.wsManager.cleanup();
    }

    this.updateStatus('Disconnected', 'disconnected');
  }

  // Handle audio received from the websocket
  handleAudioReceived(audioData: Float32Array): void {
    // In a real implementation, the WebSocketEventManager already takes care of audio playback
    console.log('Audio data received, length:', audioData.length);
    // Let voice this audio from dynamic
  }
}

// Add global type declaration for our custom property
declare global {
  interface Window {
    audioCleanup: () => void;
  }
}
